import React, { useState, useEffect } from "react";
import { useParams } from "react-router-dom";
import moment from "moment-timezone";
import axios from "axios";
import { useAuth } from "../../contexts/AuthContext";
import {
  CalendarContainer,
  NavigationHeader,
  NavButton,
  CurrentDate,
  ViewToggle,
  CalendarGrid,
  ScreeningBlock,
  DayLabel,
  TimeColumn,
  TimeMarker,
  DayColumn,
  FilmTitle,
  ScreeningInfo,
  OverlayToggleButton,
  OverlayScreeningBlock,
  BusyTimeBlock,
  BusyTimeLabel,
  OverlayMessage,
  LockIcon,
} from "./CalendarView.styled";
import { Lock } from "@mui/icons-material";

const CalendarView = ({
  mode = "per-venue",
  filmId = null,
  scheduleData = null,
  festivalData = null,
  externalLockedScreeningIds = null,
  onExternalToggleLock = null,
}) => {
  const { festivalId } = useParams();
  const { currentUser, userId, token } = useAuth();
  const [screenings, setScreenings] = useState([]);
  const [venues, setVenues] = useState([]);
  const [currentDate, setCurrentDate] = useState(null);
  const [festival, setFestival] = useState(null);
  const [loading, setLoading] = useState(true);
  const [viewMode, setViewMode] = useState(scheduleData ? "per-day" : mode);
  const [filmTitle, setFilmTitle] = useState("");
  const [showScheduleOverlay, setShowScheduleOverlay] = useState(
    () => localStorage.getItem("showScheduleOverlay") === "true",
  );
  const [userSchedule, setUserSchedule] = useState(null);
  const [scheduleLoading, setScheduleLoading] = useState(false);
  const [scheduleError, setScheduleError] = useState(null);
  const [busyTimes, setBusyTimes] = useState([]);
  const [internalLockedIds, setInternalLockedIds] = useState(new Set());

  // Use external state if provided, otherwise use internal state
  const lockedScreeningIds = externalLockedScreeningIds || internalLockedIds;

  // Fetch locked screenings only when managing our own state
  useEffect(() => {
    if (externalLockedScreeningIds || !userId || !token) return;

    const fetchLockedScreenings = async () => {
      try {
        const response = await axios.get(
          `${process.env.REACT_APP_API_BASE_URL}/api/userscreenings`,
          { headers: { Authorization: `Bearer ${token}` } },
        );
        setInternalLockedIds(new Set(response.data.map((us) => us.screening_id)));
      } catch (error) {
        setInternalLockedIds(new Set());
      }
    };

    fetchLockedScreenings();
  }, [externalLockedScreeningIds, userId, token]);

  const internalToggleLock = async (screeningId) => {
    if (!userId || !token) return;
    const isCurrentlyLocked = lockedScreeningIds.has(screeningId);

    // Optimistic update
    setInternalLockedIds((prev) => {
      const next = new Set(prev);
      if (isCurrentlyLocked) {
        next.delete(screeningId);
      } else {
        next.add(screeningId);
      }
      return next;
    });

    try {
      if (isCurrentlyLocked) {
        await axios.delete(
          `${process.env.REACT_APP_API_BASE_URL}/api/userscreenings/screening/${screeningId}`,
          { headers: { Authorization: `Bearer ${token}` } },
        );
      } else {
        await axios.post(
          `${process.env.REACT_APP_API_BASE_URL}/api/userscreenings`,
          { screening_id: screeningId },
          { headers: { Authorization: `Bearer ${token}` } },
        );
      }
    } catch (error) {
      // Revert on failure
      setInternalLockedIds((prev) => {
        const next = new Set(prev);
        if (isCurrentlyLocked) {
          next.add(screeningId);
        } else {
          next.delete(screeningId);
        }
        return next;
      });
    }
  };

  const handleToggleLock = onExternalToggleLock || internalToggleLock;

  useEffect(() => {
    if (scheduleData && festivalData) {
      setFestival(festivalData);
      setScreenings(scheduleData);
      const uniqueVenues = [
        ...new Set(scheduleData.map((s) => s.venue_name)),
      ].sort();
      setVenues(uniqueVenues);
      if (!currentDate) {
        setCurrentDate(new Date(festivalData.start_date));
      }
      setLoading(false);
      return;
    }

    const fetchData = async () => {
      try {
        setLoading(true);

        const festivalResponse = await axios.get(
          `${process.env.REACT_APP_API_BASE_URL}/api/festivals/${festivalId}`,
        );
        setFestival(festivalResponse.data);

        if (!currentDate) {
          setCurrentDate(new Date(festivalResponse.data.start_date));
        }

        const screeningsResponse = await axios.get(
          `${process.env.REACT_APP_API_BASE_URL}/api/screenings/festival/${festivalId}`,
        );

        let screeningsData = screeningsResponse.data;

        if (filmId) {
          screeningsData = screeningsData.filter(
            (screening) => screening.film_id === Number(filmId),
          );
          // Set film title from the first screening if available
          if (screeningsData.length > 0) {
            setFilmTitle(screeningsData[0].film_title);
          }
        }

        const uniqueVenues = [
          ...new Set(screeningsData.map((s) => s.venue_name)),
        ].sort();
        setVenues(uniqueVenues);

        setScreenings(screeningsData);
        setLoading(false);
      } catch (error) {
        console.error("Error fetching calendar data:", error);
        setLoading(false);
      }
    };

    fetchData();
  }, [festivalId, currentDate, filmId, scheduleData, festivalData]);

  useEffect(() => {
    if (!showScheduleOverlay || !userId || userSchedule || scheduleData) return;

    const fetchSchedule = async () => {
      try {
        setScheduleLoading(true);
        setScheduleError(null);
        const idToken = await currentUser.getIdToken();
        const response = await axios.get(
          `${process.env.REACT_APP_API_BASE_URL}/api/schedule/latest/${festivalId}`,
          {
            headers: {
              Authorization: `Bearer ${idToken}`,
            },
          },
        );
        setUserSchedule(response.data);
      } catch (error) {
        if (error.response && error.response.status === 404) {
          setScheduleError("No schedule found. Generate a schedule first.");
        } else {
          setScheduleError("Failed to load schedule.");
        }
      } finally {
        setScheduleLoading(false);
      }
    };

    fetchSchedule();
  }, [showScheduleOverlay, userId, festivalId, userSchedule, currentUser, scheduleData]);

  useEffect(() => {
    if ((!showScheduleOverlay && !scheduleData) || !userId || !token) return;

    const fetchBusyTimes = async () => {
      try {
        const response = await axios.get(
          `${process.env.REACT_APP_API_BASE_URL}/api/userfestivalschedule/${festivalId}`,
          {
            headers: {
              Authorization: `Bearer ${token}`,
            },
          },
        );
        setBusyTimes(response.data);
      } catch (error) {
        setBusyTimes([]);
      }
    };

    fetchBusyTimes();
  }, [showScheduleOverlay, scheduleData, userId, festivalId, token]);

  if (loading || !currentDate) {
    return <div>Loading...</div>;
  }

  const overlayScreenings =
    showScheduleOverlay && userSchedule && !scheduleData
      ? userSchedule.schedule_data.schedule
      : [];
  const overlayTimezone =
    showScheduleOverlay && userSchedule && !scheduleData
      ? userSchedule.schedule_data.festivalTimezone
      : null;

  return (
    <CalendarContainer>
      {!scheduleData && (
        <NavigationHeader>
          {viewMode === "per-venue" ? (
            <>
              <NavButton
                onClick={() =>
                  setCurrentDate(
                    new Date(currentDate.setDate(currentDate.getDate() - 1)),
                  )
                }
                disabled={currentDate <= new Date(festival.start_date)}
              >
                Previous Day
              </NavButton>

              <CurrentDate>
                {currentDate.toLocaleDateString("en-US", {
                  weekday: "long",
                  month: "long",
                  day: "numeric",
                })}
              </CurrentDate>

              <NavButton
                onClick={() =>
                  setCurrentDate(
                    new Date(currentDate.setDate(currentDate.getDate() + 1)),
                  )
                }
                disabled={currentDate >= new Date(festival.end_date)}
              >
                Next Day
              </NavButton>
            </>
          ) : (
            <>
              <CurrentDate>Calendar for {filmTitle} Screenings</CurrentDate>
              {currentUser && (
                <OverlayToggleButton
                  $isActive={showScheduleOverlay}
                  onClick={() =>
                    setShowScheduleOverlay((prev) => {
                      localStorage.setItem("showScheduleOverlay", String(!prev));
                      return !prev;
                    })
                  }
                  disabled={scheduleLoading}
                >
                  {scheduleLoading
                    ? "Loading..."
                    : showScheduleOverlay
                      ? "Hide Your Schedule"
                      : "Overlay Your Schedule"}
                </OverlayToggleButton>
              )}
            </>
          )}
        </NavigationHeader>
      )}

      {showScheduleOverlay && scheduleError && (
        <OverlayMessage>{scheduleError}</OverlayMessage>
      )}

      {viewMode === "per-venue" ? (
        <VenueView
          screenings={screenings}
          currentDate={currentDate}
          venues={venues}
        />
      ) : (
        <DayView
          screenings={screenings}
          festival={festival}
          overlayScreenings={overlayScreenings}
          overlayTimezone={overlayTimezone}
          busyTimes={showScheduleOverlay || scheduleData ? busyTimes : []}
          isScheduleView={!!scheduleData}
          lockedScreeningIds={lockedScreeningIds}
          onToggleLock={token ? handleToggleLock : null}
        />
      )}
    </CalendarContainer>
  );
};

const VenueView = ({ screenings, currentDate, venues }) => {
  const currentScreenings = screenings.reduce((acc, screening) => {
    const screeningDate = new Date(screening.start_time);
    if (screeningDate.toDateString() === currentDate.toDateString()) {
      if (!acc[screening.venue_name]) {
        acc[screening.venue_name] = [];
      }
      acc[screening.venue_name].push(screening);
    }
    return acc;
  }, {});

  return (
    <div>
      {venues.map((venue) => (
        <div key={venue}>
          <h3>{venue}</h3>
          {currentScreenings[venue] ? (
            currentScreenings[venue].map((screening) => (
              <p key={screening.screening_id}>{screening.film_title}</p>
            ))
          ) : (
            <p>No screenings</p>
          )}
        </div>
      ))}
    </div>
  );
};

const DayView = ({
  screenings,
  festival,
  overlayScreenings = [],
  overlayTimezone = null,
  busyTimes = [],
  isScheduleView = false,
  lockedScreeningIds = new Set(),
  onToggleLock = null,
}) => {
  if (!festival) return null;

  // Build a map of screening_id → priority from overlay screenings
  const overlayPriorityMap = new Map(
    overlayScreenings.map((s) => [s.screening_id, s.priority]),
  );

  const startDate = moment(festival.start_date).startOf("day");
  const endDate = moment(festival.end_date).startOf("day");
  const numDays = endDate.diff(startDate, "days") + 1;

  const DISPLAY_START_HOUR = 8;
  const DISPLAY_END_HOUR = 26;
  const DISPLAY_HOURS = DISPLAY_END_HOUR - DISPLAY_START_HOUR;

  const formatScreeningTime = (start, end) => {
    const startHour = start.hours();
    const endHour = end.hours();
    const startMin = start.minutes();
    const endMin = end.minutes();
    const crossesNoon = startHour < 12 && endHour >= 12;

    const getAmPm = (hour) => (hour >= 12 ? "pm" : "am");

    // If duration is exactly on the hour
    if (startMin === 0 && endMin === 0) {
      return `${startHour % 12 || 12}${
        crossesNoon ? getAmPm(startHour) : ""
      } – ${endHour % 12 || 12}${getAmPm(endHour)}`;
    }

    // If start is on hour but end isn't
    if (startMin === 0) {
      return `${startHour % 12 || 12}${
        crossesNoon ? getAmPm(startHour) : ""
      } – ${endHour % 12 || 12}:${endMin.toString().padStart(2, "0")}${getAmPm(
        endHour,
      )}`;
    }

    // If neither is on hour
    return `${startHour % 12 || 12}:${startMin.toString().padStart(2, "0")}${
      crossesNoon ? getAmPm(startHour) : ""
    } – ${endHour % 12 || 12}:${endMin.toString().padStart(2, "0")}${getAmPm(
      endHour,
    )}`;
  };

  const calculatePosition = (mTime) => {
    let hours = mTime.hours();
    const minutes = mTime.minutes();

    if (hours < DISPLAY_START_HOUR) {
      hours += 24;
    }

    const totalMinutes = (hours - DISPLAY_START_HOUR) * 60 + minutes;
    return (totalMinutes * 100) / (DISPLAY_HOURS * 60);
  };

  // Clamp start/end positions to visible display range [0%, 100%].
  // matchType: "same" = item is on its own day, "spillover" = item starts
  // after midnight and shows in the previous day's late-night section.
  const clampPositions = (sp, ep, matchType) => {
    if (sp > ep) {
      if (matchType === "spillover") {
        // Spillover: start is correct (after-midnight zone), clamp end to 100%
        ep = 100;
      } else {
        // Own day: item starts before display range, clamp start to 0%
        sp = 0;
      }
    }
    return {
      startPercent: Math.max(0, Math.min(100, sp)),
      endPercent: Math.max(0, Math.min(100, ep)),
    };
  };

  // Google Calendar-style overlap layout algorithm
  const calculateOverlapLayout = (items) => {
    if (items.length === 0) return [];

    const sorted = [...items].sort(
      (a, b) =>
        a.startPercent - b.startPercent ||
        b.endPercent - a.endPercent,
    );

    // Find connected groups of overlapping items
    const groups = [];
    let currentGroup = [sorted[0]];
    let groupEnd = sorted[0].endPercent;

    for (let i = 1; i < sorted.length; i++) {
      if (sorted[i].startPercent < groupEnd) {
        currentGroup.push(sorted[i]);
        groupEnd = Math.max(groupEnd, sorted[i].endPercent);
      } else {
        groups.push(currentGroup);
        currentGroup = [sorted[i]];
        groupEnd = sorted[i].endPercent;
      }
    }
    groups.push(currentGroup);

    // For each group, greedily assign columns
    for (const group of groups) {
      const columns = [];
      for (const item of group) {
        let placed = false;
        for (let col = 0; col < columns.length; col++) {
          const lastInCol = columns[col][columns[col].length - 1];
          if (item.startPercent >= lastInCol.endPercent) {
            columns[col].push(item);
            item.column = col;
            placed = true;
            break;
          }
        }
        if (!placed) {
          item.column = columns.length;
          columns.push([item]);
        }
      }
      const totalCols = columns.length;
      for (const item of group) {
        item.totalColumns = totalCols;
      }
    }

    return sorted;
  };

  // Returns "same" if item is on this day, "spillover" if it starts
  // after midnight and belongs in the previous day's late-night section,
  // or null if it doesn't belong in this column.
  const getDayMatch = (startTime, tz, dayIndex) => {
    const itemDate = moment.tz(startTime, tz).startOf("day");
    const colDate = moment(startDate)
      .tz(tz)
      .add(dayIndex, "days")
      .startOf("day");
    if (itemDate.isSame(colDate)) return "same";
    if (
      itemDate.isSame(colDate.clone().add(1, "days")) &&
      moment.tz(startTime, tz).hours() < 2
    )
      return "spillover";
    return null;
  };

  return (
    <CalendarGrid $numDays={numDays}>
      <DayLabel style={{ gridColumn: 1, gridRow: 1 }} />
      {/* Day labels */}
      {Array.from({ length: numDays }, (_, i) => (
        <DayLabel key={i} style={{ gridColumn: i + 2, gridRow: 1 }}>
          <span className="day-name">
            {moment(startDate).add(i, "days").format("ddd")}
          </span>
          <span className="date">
            {moment(startDate).add(i, "days").format("D")}
          </span>
        </DayLabel>
      ))}

      {/* Time column */}
      <TimeColumn>
        {Array.from({ length: DISPLAY_HOURS + 1 }, (_, i) => {
          const hour = DISPLAY_START_HOUR + i;
          const displayHour = hour >= 24 ? hour - 24 : hour;
          const amPm = hour >= 24 ? "AM" : hour >= 12 ? "PM" : "AM";
          const display12Hour = displayHour % 12 || 12;

          return (
            <TimeMarker
              key={i}
              style={{
                top: `${(i * 100) / DISPLAY_HOURS}%`,
              }}
            >
              {`${display12Hour}${amPm}`}
            </TimeMarker>
          );
        })}
      </TimeColumn>

      {/* Day columns with screenings */}
      {Array.from({ length: numDays }, (_, dayIndex) => {
        // Build unified items array for overlap layout
        const allItems = [];

        overlayScreenings.forEach((s) => {
          const tz = overlayTimezone || s.festival_timezone;
          const match = getDayMatch(s.start_time, tz, dayIndex);
          if (!match) return;
          const sStart = moment.tz(s.start_time, tz);
          const sEnd = moment.tz(s.end_time, tz);
          const pos = clampPositions(
            calculatePosition(sStart),
            calculatePosition(sEnd),
            match,
          );
          if (pos.endPercent - pos.startPercent <= 0) return;
          allItems.push({
            type: "overlay",
            key: `overlay-${s.screening_id}-${match}`,
            data: s,
            ...pos,
            startMoment: sStart,
            endMoment: sEnd,
          });
        });

        busyTimes.forEach((bt) => {
          const tz = festival.timezone;
          const match = getDayMatch(bt.start_datetime, tz, dayIndex);
          if (!match) return;
          const btStart = moment.tz(bt.start_datetime, tz);
          const btEnd = moment.tz(bt.end_datetime, tz);
          const pos = clampPositions(
            calculatePosition(btStart),
            calculatePosition(btEnd),
            match,
          );
          if (pos.endPercent - pos.startPercent <= 0) return;
          allItems.push({
            type: "busy",
            key: `busy-${bt.id}-${match}`,
            data: bt,
            ...pos,
            startMoment: btStart,
            endMoment: btEnd,
          });
        });

        screenings.forEach((screening) => {
          const match = getDayMatch(
            screening.start_time,
            screening.timezone,
            dayIndex,
          );
          if (!match) return;
          const sStart = moment.tz(screening.start_time, screening.timezone);
          const sEnd = moment.tz(screening.end_time, screening.timezone);
          const pos = clampPositions(
            calculatePosition(sStart),
            calculatePosition(sEnd),
            match,
          );
          if (pos.endPercent - pos.startPercent <= 0) return;
          allItems.push({
            type: "screening",
            key: `screening-${screening.screening_id}-${match}`,
            data: screening,
            ...pos,
            startMoment: sStart,
            endMoment: sEnd,
          });
        });

        const layoutItems = calculateOverlapLayout(allItems);

        return (
          <DayColumn
            key={dayIndex}
            style={{ gridColumn: dayIndex + 2, gridRow: 2 }}
          >
            {layoutItems.map((item) => {
              const posStyle = {
                top: `${item.startPercent}%`,
                height: `${item.endPercent - item.startPercent}%`,
                left: `calc(${(item.column / item.totalColumns) * 100}% + 2px)`,
                width: `calc(${(1 / item.totalColumns) * 100}% - 4px)`,
              };

              if (item.type === "overlay") {
                return (
                  <OverlayScreeningBlock
                    key={item.key}
                    $priority={item.data.priority}
                    style={posStyle}
                  >
                    <FilmTitle>{item.data.film_title}</FilmTitle>
                    <ScreeningInfo>
                      <span>
                        {formatScreeningTime(item.startMoment, item.endMoment)}
                      </span>
                      <span>{item.data.venue_name}</span>
                    </ScreeningInfo>
                  </OverlayScreeningBlock>
                );
              }

              if (item.type === "busy") {
                return (
                  <BusyTimeBlock key={item.key} style={posStyle}>
                    <BusyTimeLabel>
                      <FilmTitle>Busy Time</FilmTitle>
                      <ScreeningInfo>
                        <span>
                          {formatScreeningTime(item.startMoment, item.endMoment)}
                        </span>
                      </ScreeningInfo>
                    </BusyTimeLabel>
                  </BusyTimeBlock>
                );
              }

              const screeningPriority = isScheduleView
                ? (item.data.priority ?? 0)
                : overlayPriorityMap.has(item.data.screening_id)
                  ? overlayPriorityMap.get(item.data.screening_id)
                  : undefined;

              const isLocked = lockedScreeningIds.has(item.data.screening_id);

              return (
                <ScreeningBlock
                  key={item.key}
                  $priority={screeningPriority}
                  $isLocked={isLocked}
                  $isClickable={!!onToggleLock}
                  style={posStyle}
                  onClick={onToggleLock ? () => onToggleLock(item.data.screening_id) : undefined}
                >
                  {isLocked && (
                    <LockIcon>
                      <Lock />
                    </LockIcon>
                  )}
                  <FilmTitle>{item.data.film_title}</FilmTitle>
                  <ScreeningInfo>
                    <span>
                      {formatScreeningTime(item.startMoment, item.endMoment)}
                    </span>
                    <span>{item.data.venue_name}</span>
                  </ScreeningInfo>
                </ScreeningBlock>
              );
            })}
          </DayColumn>
        );
      })}
    </CalendarGrid>
  );
};

export default CalendarView;
